import React from 'react';
import classNames from 'classnames/bind';
import { Group, Pill, PillProps, Popover } from '@mantine/core';

import { Tag } from 'types/tag';

import { useTags } from 'tags/hooks/useTags';

import { Button } from 'components/Button/Button';

import { TagsTree } from '../TagsTree/TagsTree';
import styles from './TagsList.module.scss';

const c = classNames.bind(styles);

export type TagsListProps = {
  className?: string;
  defaultValue?: string[];
  readOnly?: boolean;
  hideButton?: boolean;
  size?: PillProps['size'];
  onAdd?: (tag: Tag) => void;
  onRemove?: (tagId: string) => void;
};

export function TagsList({
  className,
  defaultValue = [],
  readOnly,
  hideButton,
  size,
  onAdd,
  onRemove,
}: TagsListProps) {
  const [tagIds, setTagIds] = React.useState(defaultValue);

  const { data: selectedTags, isInitialLoading } = useTags({
    params: {
      tag_ids: tagIds,
    },
    enabled: tagIds.length > 0,
  });

  const [search, setSearch] = React.useState('');

  function handleSelectValue(tag: Tag) {
    setTagIds((previous) => {
      if (previous?.includes(tag.id)) {
        return previous.filter((id) => id !== tag.id);
      }

      return [...previous, tag.id];
    });
    setSearch('');
    onAdd?.(tag);
  }

  function handleRemoveValue(tagId: Tag['id']) {
    setTagIds((previous) => {
      return previous.filter((id) => id !== tagId);
    });
    onRemove?.(tagId);
  }

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <Popover trapFocus position="bottom-start" disabled={readOnly}>
        <Group className={c(className)} gap="sm">
          {(isInitialLoading || selectedTags) && (
            <Popover.Target>
              <Pill.Group gap="xs">
                {isInitialLoading && <span className="skeleton-bar" />}

                {selectedTags &&
                  selectedTags.data.map(({ id, name }) => (
                    <Pill
                      key={id}
                      onRemove={() => handleRemoveValue(id)}
                      variant="primary"
                      size={size}
                    >
                      {name}
                    </Pill>
                  ))}
              </Pill.Group>
            </Popover.Target>
          )}

          {!readOnly && !hideButton && (
            <Popover.Target>
              <Button size="xsmall">Edit labels</Button>
            </Popover.Target>
          )}

          {hideButton && (
            <Popover.Target>
              <button className={c('trigger')} />
            </Popover.Target>
          )}
        </Group>

        {!readOnly && (
          <Popover.Dropdown>
            <TagsTree
              searchTerm={search}
              selection={selectedTags?.data.map(({ id }) => id) ?? defaultValue}
              onAdd={handleSelectValue}
              onRemove={handleRemoveValue}
            />
          </Popover.Dropdown>
        )}
      </Popover>
    </div>
  );
}
